import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { useDispatch, useSelector } from 'react-redux';
import { Carousel, Column, Container, Row } from '@pella/sash-framework';
import { SEO, Breadcrumbs, Image, CTA } from '../../components';
import { useBranchShowrooms } from '../../hooks/use-branch-showrooms';
import { useBranchData } from '../../hooks/use-branch-data';
import { getShowroomByIP } from '../../actions/location-actions';
import { setSessionStorage } from '../../components/utilities/functions';
import { useGlobalConfig } from '../../hooks/use-global-config';
import { isValidArray } from '../../components/utilities/validations';
import { useRouteChange } from '../../hooks/use-route-change';

import NearbyShowrooms from '../../components/template/branch-showroom/nearby-showrooms';
import BranchPromoPanel from '../../components/common/branch-promo-panel';
import BranchHero from '../../components/common/branch-hero';
import CardSection from '../../components/common/card-section';
import CTARibbon from '../../components/common/cta-ribbon';
import LocalNavTop from '../../components/common/local-nav-top';
import RTAPanel from '../../components/common/rta-panel';
import ProductStyleTabs from '../../components/template/branch-sem/product-style-tabs';
import BranchOfferCard from '../../components/template/branch-sem/branch-offer-card';

const BranchSEM = (props) => {
	const { sem_pella_promise } = useGlobalConfig();
	const pageData = props.data.contentstackSem;
	const branches = useBranchData();
	const dispatch = useDispatch();
	const locations = useSelector((state) => state.locations);
	const [branchID, setBranchID] = useState({ status: 'loading', id: null });
	const [branchData, setBranchData] = useState({});
	const { showrooms, error } = useBranchShowrooms(branchData?.branch_retail, null, branchData?.additional_showrooms);

	useEffect(() => {
		if (branchID.status === 'complete') return;

		setSessionStorage('sem_referral', true);

		let params = new URLSearchParams(props.location.search);
		let branch = params.get('branch');

		if (pageData.branch_id?.[0]?.branch_retail?.[0]) {
			setBranchID({ status: 'complete', id: pageData.branch_id[0].branch_retail });
			setBranchData(branches?.find((branchEntry) => pageData.branch_id[0].branch_retail?.some((branchID) => branchEntry.branch_retail?.includes(branchID))));
		} else if (branch && branch.length === 3) {
			setBranchID({ status: 'complete', id: [branch] });
			setBranchData(branches?.find((branchEntry) => branchEntry.branch_retail?.includes(branch)));
		} else if (locations?.showrooms?.[0]?.branch_id) {
			setBranchID({ status: 'complete', id: [locations.showrooms[0].branch_id] });
			setBranchData(branches?.find((branch) => branch.branch_retail?.includes(locations.showrooms[0].branch_id)));
		} else {
			setBranchID({ status: 'notfound', id: null });
			dispatch(getShowroomByIP());
		}
	}, [props.location.search, branchID.status, branches, pageData, locations?.showrooms, dispatch]);

	useRouteChange({
		pageTitle: pageData.seo?.title,
		pageType: '',
		userData: {
			branchID: branchID.id?.[0],
		},
	});

	return (
		<div
			id='template-BranchSEM'
			className='relative !h-auto'
		>
			{!pageData.remove_local_nav && isValidArray(branchData?.local_nav) && (
				<LocalNavTop
					branchData={branchData}
					linksArray={branchData.local_nav}
					isAnchors={false}
					className='!top-0'
					overRidePhone='833-404-6788'
				/>
			)}

			{pageData.breadcrumbs && (
				<Breadcrumbs
					data={pageData.breadcrumbs}
					currentUrl={pageData.url}
				/>
			)}

			{(pageData.page_heading?.heading || pageData.page_heading?.add_local_heading) && (
				<Container className='py-6 text-center md:py-8 xl:py-12'>
					{pageData.page_heading.heading && (
						<h1
							className='m-0 font-bold'
							dangerouslySetInnerHTML={{ __html: pageData.page_heading.heading }}
						/>
					)}
					{pageData.page_heading.add_local_heading && branchData?.formal_name && (
						<h1
							className='m-0 font-bold'
							dangerouslySetInnerHTML={{ __html: branchData.formal_name }}
						/>
					)}
				</Container>
			)}

			{isValidArray(pageData.modular_blocks) &&
				pageData.modular_blocks.map((block, index) => {
					if (block.hero != null) {
						return (
							<BranchHero
								data={block.hero}
								key={index}
								className='[&_h1]:text-2xl [&_h1]:font-semibold [&_h1]:normal-case [&_h1]:tracking-normal xl:[&_h1]:text-3xl [&_p]:text-sm xl:[&_p]:text-lg'
							/>
						);
					}

					if (block.columns != null && isValidArray(block.columns?.content) && !block.columns.carousel) {
						return (
							<div
								key={index}
								className='semColumns py-6 md:py-8 xl:py-12'
								style={{ backgroundColor: block.columns.background_hex_value }}
							>
								<Container>
									{block.columns.content.map((row, index) => (
										<Row
											key={index}
											className='semRow'
											justify='center'
											align={row.align}
											verticalGutters
										>
											{isValidArray(row.columns) &&
												row.columns.map((col, index) => {
													const colTextClass = col.container_hex_value === '#000000' ? 'text-white [&_a]:text-white' : '';

													return (
														<Column
															key={index}
															xs={12}
															lg={col.column_desktop}
														>
															<div
																className='semColumn-content h-full'
																style={{ backgroundColor: col.container_hex_value }}
															>
																{isValidArray(col.image) && (
																	<Image
																		data={col.image[0]}
																		className='!block'
																	/>
																)}

																<div className={col.container_hex_value ? 'p-5' : ''}>
																	{col.subheading && (
																		<div
																			className={`mb-1 text-sm font-semibold uppercase ${colTextClass}`}
																			dangerouslySetInnerHTML={{ __html: col.subheading }}
																		/>
																	)}
																	{col.heading && (
																		<h2
																			className={`h3 my-0 ${colTextClass}`}
																			dangerouslySetInnerHTML={{ __html: col.heading }}
																		/>
																	)}
																	{col.description && (
																		<div
																			className={colTextClass}
																			dangerouslySetInnerHTML={{ __html: col.description }}
																		/>
																	)}
																	{isValidArray(col.cta) && (
																		<CTA
																			data={col.cta[0]}
																			className='mt-4'
																		/>
																	)}
																</div>
															</div>
														</Column>
													);
												})}
										</Row>
									))}
								</Container>
							</div>
						);
					}

					if (block.columns != null && isValidArray(block.columns?.content) && block.columns.carousel) {
						return (
							<div
								key={index}
								className='semColumns py-6 md:py-8 xl:py-12'
								style={{ backgroundColor: block.columns.background_hex_value }}
							>
								<Container width={1300}>
									<Carousel
										key={block.columns.content.length}
										showArrows
									>
										{block.columns.content.map((row, index) => (
											<Row
												key={index}
												noGutters
												reverse
											>
												{isValidArray(row.columns) &&
													row.columns.map((col, index) => {
														const colTextClass = col.container_hex_value === '#000000' ? 'text-white [&_a]:text-white' : '';

														return (
															<Column
																xs={12}
																lg={col.column_desktop}
																key={index}
															>
																<div
																	className='semColumn-content h-full overflow-hidden'
																	style={{ backgroundColor: col.container_hex_value }}
																>
																	{isValidArray(col.image) && (
																		<Image
																			data={col.image[0]}
																			className='!block max-md:aspect-[3/2] md:h-full'
																		/>
																	)}

																	{(col.subheading || col.heading || col.description || col.cta?.[0]) && (
																		<div className='p-6 md:flex md:h-full md:flex-col md:justify-center md:px-8 md:py-12'>
																			{col.subheading && (
																				<div
																					className={`mb-0.5 text-sm font-semibold uppercase tracking-2px text-primary`}
																					dangerouslySetInnerHTML={{ __html: col.subheading }}
																				/>
																			)}
																			{col.heading && (
																				<h2
																					className={`mb-4 mt-0 !text-2xl font-semibold md:mb-6 ${colTextClass}`}
																					dangerouslySetInnerHTML={{ __html: col.heading }}
																				/>
																			)}
																			{col.description && (
																				<div
																					className={`${colTextClass} text-base`}
																					dangerouslySetInnerHTML={{ __html: col.description }}
																				/>
																			)}
																			{isValidArray(col.cta) && (
																				<CTA
																					data={col.cta[0]}
																					className='mt-4'
																				/>
																			)}
																		</div>
																	)}
																</div>
															</Column>
														);
													})}
											</Row>
										))}
									</Carousel>
								</Container>
							</div>
						);
					}

					if (block.local_content != null && block.local_content.local_content_type === 'nearby' && isValidArray(branchID.id) && isValidArray(showrooms)) {
						return (
							<Container
								key={index}
								className='py-6 md:py-8 xl:py-12'
							>
								{block.local_content.heading && (
									<h2
										className='mb-6 text-center font-semibold'
										dangerouslySetInnerHTML={{ __html: block.local_content.heading }}
									/>
								)}

								{block.local_content.subheading && <div dangerouslySetInnerHTML={{ __html: block.local_content.subheading }} />}

								{block.local_content.description && (
									<div
										className='mb-3'
										dangerouslySetInnerHTML={{ __html: block.local_content.description }}
									/>
								)}

								<NearbyShowrooms
									locations={showrooms}
									key={showrooms?.length}
									error={error}
								/>
							</Container>
						);
					}

					if (block.local_content != null && block.local_content.local_content_type === 'offer' && isValidArray(branchID.id) && !branchData?.hide_promo) {
						return (
							<BranchPromoPanel
								branchID={branchID.id}
								key={index}
								showNational={block.local_content.national_offer}
							/>
						);
					}

					if (block.cards != null) {
						return (
							<CardSection
								data={block.cards}
								key={index}
							/>
						);
					}

					if (block.reviews != null && isValidArray(block.reviews.group)) {
						return (
							<div
								key={index}
								className='semReviews py-6 md:py-8 xl:py-12'
							>
								<Container>
									{block.reviews.heading && <h2 className='text-center font-semibold'>{block.reviews.heading}</h2>}

									<Carousel
										dotsClassName='!mt-0'
										showDots
									>
										{block.reviews.group.map((review, index) => (
											<blockquote
												key={index}
												className='!pb-0 before:content-none'
											>
												<div dangerouslySetInnerHTML={{ __html: review.quote }} />
												<cite dangerouslySetInnerHTML={{ __html: review.citation }} />
											</blockquote>
										))}
									</Carousel>
								</Container>
							</div>
						);
					}

					if (block.cta_ribbon != null && isValidArray(block.cta_ribbon.cta_ribbon)) {
						return (
							<div
								key={index}
								className='py-6'
							>
								<Container>
									{block.cta_ribbon.heading && (
										<h2
											className='font-semibold'
											dangerouslySetInnerHTML={{ __html: block.cta_ribbon.heading }}
										/>
									)}
									<CTARibbon data={block.cta_ribbon.cta_ribbon[0]} />
								</Container>
							</div>
						);
					}

					if (block.rta != null && !branchData?.hide_rta) {
						return (
							<RTAPanel
								key={index}
								data={block.rta}
								className='py-4 md:py-6'
								src='ppc'
							/>
						);
					}

					if (block.window_101 != null) {
						return (
							<div
								key={index}
								className='py-6 md:py-8 xl:py-12'
							>
								<Container width={1300}>
									{block.window_101.heading && <h2 className='mb-3 text-center font-semibold'>{block.window_101.heading}</h2>}
									{block.window_101.subheading && <p className='mb-6 text-center xl:mb-10'>{block.window_101.subheading}</p>}
									{isValidArray(block.window_101.carousel) && <ProductStyleTabs data={block.window_101.carousel} />}
									{isValidArray(block.window_101.cta) && (
										<div className='mt-6 text-center xl:mt-10'>
											<CTA data={block.window_101.cta[0]} />
										</div>
									)}
								</Container>
							</div>
						);
					}

					if (block.image_carousel != null) {
						return (
							<div
								key={index}
								className='semImageCarousel py-4 md:py-6 xl:py-10'
							>
								<Container>
									<Row
										reverse={block.image_carousel.image_position === 'right'}
										largeGutters
										verticalGutters
									>
										<Column
											xs={12}
											md='fluid'
										>
											{isValidArray(block.image_carousel.image) && (
												<Carousel
													wrapperClassName='md:h-full md:[&>div]:h-full md:[&>div>ul]:h-full'
													key={block.image_carousel.image.length}
													showArrows
												>
													{block.image_carousel.image.map((image, index) => (
														<Image
															data={image}
															key={index}
															className='!block aspect-[3.5/2] md:h-full'
															objectPosition='center'
														/>
													))}
												</Carousel>
											)}
										</Column>

										<Column
											xs={12}
											className='md:flex-[0_0_300px] xl:flex-[0_0_450px]'
										>
											{block.image_carousel.description && (
												<div
													className='mb-4 xl:mb-6'
													dangerouslySetInnerHTML={{ __html: block.image_carousel.description }}
												/>
											)}
											{isValidArray(block.image_carousel.cta) && (
												<CTA
													size='sm'
													data={block.image_carousel.cta[0]}
												/>
											)}

											{block.image_carousel.include_local_offer && (
												<BranchOfferCard
													branchID={branchID.id}
													className='mt-6'
												/>
											)}
										</Column>
									</Row>
								</Container>
							</div>
						);
					}

					if (block.config_cards != null && isValidArray(sem_pella_promise?.card)) {
						return (
							<CardSection
								data={{
									...sem_pella_promise,
									heading: block.config_cards.heading,
								}}
								key={index}
							/>
						);
					}
				})}
		</div>
	);
};

BranchSEM.propTypes = {
	data: PropTypes.object.isRequired,
	location: PropTypes.object,
};

export default BranchSEM;

// eslint-disable-next-line react/prop-types -- gatsby component
export const Head = ({ data }) => <SEO {...data.contentstackSem.seo} />;

export const query = graphql`
	query ($uid: String!) {
		contentstackSem(id: { eq: $uid }) {
			url
			remove_local_nav
			page_heading {
				add_local_heading
				heading
			}
			branch_id {
				...BranchData
			}
			modular_blocks {
				hero {
					image {
						...Image
						image {
							url
							gatsbyImageData(quality: 90, layout: FULL_WIDTH)
						}
					}
					heading
					description
					cta {
						...CTA
					}
				}
				columns {
					carousel
					background_hex_value
					content {
						align
						columns {
							column_desktop
							container_hex_value
							image {
								...Image
								image {
									url
									gatsbyImageData(quality: 90)
								}
							}
							subheading
							heading
							description
							cta {
								...CTA
							}
						}
					}
				}
				local_content {
					local_content_type
					national_offer
					by_state
					heading
					subheading
					description
				}
				cards {
					...CardSection
				}
				reviews {
					heading
					group {
						quote
						citation
					}
				}
				cta_ribbon {
					heading
					cta_ribbon {
						...CTARibbon
					}
				}
				rta {
					heading
					subheading
					description
					form_heading
				}
				image_carousel {
					include_local_offer
					description
					cta {
						...CTA
					}
					image_position
					image {
						...Image
						image {
							url
							gatsbyImageData(quality: 90)
						}
					}
				}
				window_101 {
					heading
					subheading
					carousel {
						heading
						content {
							image {
								...Image
								image {
									url
									gatsbyImageData(quality: 90)
								}
							}
							heading
							description
							cta {
								...CTA
							}
						}
					}
					cta {
						...CTA
					}
				}
				config_cards {
					heading
				}
			}
			seo {
				...SEO
			}
		}
	}
`;
